import * as XLSX from "xlsx";
import { serviceResponseTransformation } from "./download-utils/transformDataToExport";
import { saveFileWithPicker } from "../../utils/fileManagerFunctions";
import { generateFilename } from "./download-utils/generateFilenameToExport";

export const exportToXLS = (serviceResponses, filename) => {
  if (!Array.isArray(serviceResponses)) {
    serviceResponses = [serviceResponses];
  }

  const formatValue = (value) => {
    if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) {
        return value.map(formatValue).join("\n");
      }
      return Object.entries(value)
        .map(([key, val]) => `${key}: ${formatValue(val)}`)
        .join("\n");
    }

    const lines = String(value).split("\n");
    if (lines.length > 1) {
      return lines.map((line, index) => `${index + 1}. ${line.trim()}`).join("\n");
    }
    return value;
  };

  const workbook = XLSX.utils.book_new();

  serviceResponses.forEach((serviceResponse, index) => {
    if (!serviceResponse) {
      return;
    }

    const sheetTitle = `${filename} - ${index + 1}`;
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    let headers = [];
    let values = [];

    if (typeof serviceResponse === "string") {
      headers = ["User Story"];
      values = [[formatValue(serviceResponse)]];
    } else if (typeof serviceResponse === "object" && !Array.isArray(serviceResponse)) {
      headers = Object.keys(serviceResponse);
      values = [Object.values(serviceResponse).map(formatValue)];
    } else {
      const transformedData = serviceResponseTransformation(serviceResponse, filename, "excel") || {};
      headers = transformedData.headers || [];
      values = transformedData.values || [];

      values = values.map(row => {
        while (row.length < headers.length) row.push("");
        return row.map(formatValue);
      });
    }

    if (!headers || !values || values.length === 0) {
      return;
    }

    // Add headers and data
    XLSX.utils.sheet_add_aoa(worksheet, [headers, ...values], { origin: "A1" });

    // **Auto-adjust column width based on content length**
    const columnWidths = headers.map((_, colIdx) => ({
      wch: Math.max(
        9, // Minimum width
        ...values.map(row => (row[colIdx] ? row[colIdx].length * 1.5 : 10)) // Adjust multiplier
      ),
    }));
    worksheet["!cols"] = columnWidths;

    // **Force multi-line display immediately by setting row height**
    worksheet["!rows"] = values.map(row => ({
      hpt: Math.max(20, ...row.map(cell => (typeof cell === "string" ? (cell.split("\n").length * 15) : 20))) // Adjust line height multiplier
    }));

    // **Enable wrap text & apply formatting to all cells**
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = {
            alignment: { wrapText: true, vertical: "top", horizontal: "left" },
            font: { name: "Calibri", sz: 11 },
          };
        }
      }
    }

    // **Append the sheet and save the file**
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);
  });

  // **Force Excel-specific options to ensure wrap text is immediately applied**
  // XLSX.writeFile(workbook, `${filename}.xlsx`, { bookType: "xlsx", cellStyles: true });

  const wbout = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });
  
  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const extendedFileName = generateFilename(filename, "xlsx");
  saveFileWithPicker(blob, extendedFileName);
};
